import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Wrench, FileText, Package, AlertTriangle, Plus, Trash2, Activity, Clock, DollarSign, Layers, Zap, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  mount: { label: "Montagem", color: "bg-blue-100 text-blue-800", icon: Wrench },
  unmount: { label: "Desmontagem", color: "bg-slate-100 text-slate-800", icon: Wrench },
  maintenance_preventive: { label: "Manutenção Preventiva", color: "bg-emerald-100 text-emerald-800", icon: Wrench },
  maintenance_corrective: { label: "Manutenção Corretiva", color: "bg-amber-100 text-amber-800", icon: Wrench },
  repair: { label: "Reparação", color: "bg-red-100 text-red-800", icon: Wrench },
  inspection: { label: "Inspeção", color: "bg-purple-100 text-purple-800", icon: Wrench },
};

function KPI({ label, value, sub, icon: Icon, color = "#0b1f3a" }) {
  return (
    <div className="bg-white border rounded-lg p-3 shadow-sm">
      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2xl font-black mt-0.5" style={{ color }}>{value ?? "-"}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function MoldFiche() {
  const { moldId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEventDlg, setShowEventDlg] = useState(false);
  const [showSpareDlg, setShowSpareDlg] = useState(false);
  const [showLocDlg, setShowLocDlg] = useState(false);
  const [showPlanDlg, setShowPlanDlg] = useState(false);
  const [showDocDlg, setShowDocDlg] = useState(false);
  const [evForm, setEvForm] = useState({ event_type: "maintenance_preventive", machine_code: "", duration_minutes: 0, cost: 0, technician: "", notes: "" });
  const [spForm, setSpForm] = useState({ name: "", part_number: "", quantity_in_stock: 0, min_quantity: 1, supplier: "", unit_cost: 0 });
  const [locForm, setLocForm] = useState({ location: "", notes: "" });
  const [planForm, setPlanForm] = useState({ every_cycles: 10000, every_days: 90, tasks: [""] });
  const [docForm, setDocForm] = useState({ doc_type: "photo", title: "", url: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/molds-ext/${moldId}/full`);
      setData(r.data);
      if (r.data.location) setLocForm({ location: r.data.location, notes: r.data.mold?.location_notes || "" });
      if (r.data.preventive_plan?.every_cycles) setPlanForm({
        every_cycles: r.data.preventive_plan.every_cycles || 10000,
        every_days: r.data.preventive_plan.every_days || 90,
        tasks: r.data.preventive_plan.tasks || [""],
      });
    } catch (e) { toast.error("Erro a carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [moldId]);

  const addEvent = async () => {
    try {
      await axios.post(`${API}/molds-ext/${moldId}/events`, { ...evForm, cost: parseFloat(evForm.cost) || null, duration_minutes: parseInt(evForm.duration_minutes) || null });
      toast.success("Evento registado");
      setShowEventDlg(false);
      setEvForm({ event_type: "maintenance_preventive", machine_code: "", duration_minutes: 0, cost: 0, technician: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const delEvent = async (id) => {
    if (!window.confirm("Apagar evento?")) return;
    try { await axios.delete(`${API}/molds-ext/${moldId}/events/${id}`); toast.success("Apagado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const addSpare = async () => {
    try {
      await axios.post(`${API}/molds-ext/${moldId}/spare-parts`, spForm);
      toast.success("Sobressalente adicionado");
      setShowSpareDlg(false);
      setSpForm({ name: "", part_number: "", quantity_in_stock: 0, min_quantity: 1, supplier: "", unit_cost: 0 });
      load();
    } catch (e) { toast.error("Erro"); }
  };
  const delSpare = async (id) => {
    if (!window.confirm("Apagar?")) return;
    await axios.delete(`${API}/molds-ext/${moldId}/spare-parts/${id}`);
    load();
  };
  const saveLocation = async () => {
    try { await axios.post(`${API}/molds-ext/${moldId}/location`, locForm); toast.success("Localização atualizada"); setShowLocDlg(false); load(); }
    catch (e) { toast.error("Erro"); }
  };
  const savePlan = async () => {
    try { await axios.post(`${API}/molds-ext/${moldId}/preventive-plan`, { ...planForm, tasks: planForm.tasks.filter(t => t.trim()) });
      toast.success("Plano guardado"); setShowPlanDlg(false); load(); }
    catch (e) { toast.error("Erro"); }
  };
  const addDoc = async () => {
    if (!docForm.title || !docForm.url) { toast.error("Preenche título e URL"); return; }
    try { await axios.post(`${API}/molds-ext/${moldId}/documents`, docForm); toast.success("Documento adicionado"); setShowDocDlg(false); setDocForm({ doc_type: "photo", title: "", url: "", notes: "" }); load(); }
    catch (e) { toast.error("Erro"); }
  };

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="p-10 text-center">Molde não encontrado</div>;

  const { mold, statistics: s, events, spare_parts, preventive_plan, alerts, documents, inserts, hot_runners } = data;
  const lifeColor = s.life_pct >= 90 ? "#dc2626" : s.life_pct >= 75 ? "#f59e0b" : "#16a34a";

  return (
    <div className="space-y-4" data-testid="mold-fiche-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-3xl font-bold">{mold.code} — {mold.description}</h1>
          <div className="text-sm text-muted-foreground flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{data.location || "Sem localização"}</span>
            <button onClick={() => setShowLocDlg(true)} className="text-blue-600 text-xs underline" data-testid="edit-location">alterar</button>
          </div>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`p-3 rounded-lg border flex items-center gap-2 text-sm ${a.severity === "critical" ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">{a.text}</span>
              <Button size="sm" variant="outline" className="bg-white" data-testid="create-mo-from-alert"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/cmms/orders`, {
                      title: `Manutenção ${mold.code} — ${a.text}`,
                      type: "preventive",
                      priority: a.severity === "critical" ? "critical" : "high",
                      target_type: "mold",
                      target_id: mold.id,
                      target_code: mold.code,
                      description: `Alerta automático: ${a.text}`,
                    });
                    toast.success(`Ordem MO criada. Ver em /cmms`);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
                }}>
                <Wrench className="h-3 w-3 mr-1" /> Criar MO
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Ciclos" value={s.cycles_current?.toLocaleString()} sub={`/ ${s.cycles_life?.toLocaleString()}`} icon={Activity} />
        <KPI label="Vida útil %" value={`${s.life_pct}%`} color={lifeColor} icon={Activity} />
        <KPI label="Cavidades" value={mold.cavities} icon={Layers} />
        <KPI label="Horas" value={s.hours_used} sub="montado" icon={Clock} />
        <KPI label="Custo total" value={`${s.total_cost}€`} icon={DollarSign} />
        <KPI label="Eventos" value={s.events_count} icon={Wrench} />
      </div>

      {/* Two-column layout */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Events */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2"><Wrench className="h-5 w-5" /> Histórico ({events.length})</h2>
            <Button size="sm" onClick={() => setShowEventDlg(true)} data-testid="add-event-btn"><Plus className="h-4 w-4 mr-1" /> Evento</Button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {events.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Sem eventos</div>}
            {events.map(e => {
              const meta = EVENT_LABELS[e.event_type] || { label: e.event_type, color: "bg-slate-100" };
              return (
                <div key={e.id} className="p-2 border rounded flex justify-between items-start gap-2 text-sm">
                  <div className="flex-1">
                    <div className="flex gap-2 items-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>
                      <span className="text-xs text-slate-500">{new Date(e.timestamp).toLocaleString("pt-PT")}</span>
                    </div>
                    <div className="text-xs text-slate-700 mt-1">
                      {e.machine_code && <>Máq: <b>{e.machine_code}</b> · </>}
                      {e.duration_minutes ? <>{e.duration_minutes}min · </> : null}
                      {e.cost ? <>{e.cost}€ · </> : null}
                      {e.technician && <>Téc: {e.technician} · </>}
                      {e.notes}
                    </div>
                  </div>
                  <button onClick={() => delEvent(e.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Spare parts */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2"><Package className="h-5 w-5" /> Sobressalentes ({spare_parts.length})</h2>
            <Button size="sm" onClick={() => setShowSpareDlg(true)} data-testid="add-spare-btn"><Plus className="h-4 w-4 mr-1" /> Peça</Button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {spare_parts.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Sem sobressalentes</div>}
            {spare_parts.map(sp => (
              <div key={sp.id} className="p-2 border rounded flex justify-between items-center text-sm">
                <div>
                  <div className="font-medium">{sp.name} {sp.part_number && <span className="text-xs text-slate-500 font-mono">({sp.part_number})</span>}</div>
                  <div className="text-xs text-slate-500">
                    Stock: <b className={sp.quantity_in_stock < sp.min_quantity ? "text-red-600" : ""}>{sp.quantity_in_stock}</b> / min {sp.min_quantity}
                    {sp.unit_cost ? ` · ${sp.unit_cost}€` : ""}
                    {sp.supplier ? ` · ${sp.supplier}` : ""}
                  </div>
                </div>
                <button onClick={() => delSpare(sp.id)} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Preventive plan */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2"><Zap className="h-5 w-5" /> Plano Preventivo</h2>
            <Button size="sm" variant="outline" onClick={() => setShowPlanDlg(true)} data-testid="edit-plan-btn">Editar</Button>
          </div>
          {preventive_plan?.every_cycles ? (
            <div className="text-sm space-y-1">
              <div>Cada <b>{preventive_plan.every_cycles?.toLocaleString()}</b> ciclos</div>
              {preventive_plan.every_days && <div>ou cada <b>{preventive_plan.every_days}</b> dias</div>}
              <div className="mt-2 font-semibold text-xs uppercase text-slate-500">Tarefas:</div>
              <ul className="list-disc ml-5 text-xs text-slate-700">
                {preventive_plan.tasks?.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          ) : <div className="text-sm text-slate-400 text-center py-4">Sem plano definido</div>}
        </div>

        {/* Documents */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2"><FileText className="h-5 w-5" /> Documentos ({documents.length})</h2>
            <Button size="sm" onClick={() => setShowDocDlg(true)} data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-1" /> Doc</Button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {documents.length === 0 && <div className="text-sm text-slate-400 text-center py-4">Sem documentos</div>}
            {documents.map(d => (
              <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="p-2 border rounded flex justify-between items-center text-sm hover:bg-slate-50">
                <div><b>{d.title}</b> <span className="text-xs text-slate-500">· {d.doc_type}</span></div>
                <span className="text-xs text-blue-600">abrir ↗</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* --- DIALOGS --- */}
      <Dialog open={showEventDlg} onOpenChange={setShowEventDlg}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle>Registar Evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo</Label>
              <Select value={evForm.event_type} onValueChange={(v) => setEvForm({ ...evForm, event_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Máquina</Label><Input value={evForm.machine_code} onChange={e => setEvForm({ ...evForm, machine_code: e.target.value })} /></div>
              <div><Label>Duração (min)</Label><Input type="number" value={evForm.duration_minutes} onChange={e => setEvForm({ ...evForm, duration_minutes: e.target.value })} /></div>
              <div><Label>Custo (€)</Label><Input type="number" step="0.01" value={evForm.cost} onChange={e => setEvForm({ ...evForm, cost: e.target.value })} /></div>
              <div><Label>Técnico</Label><Input value={evForm.technician} onChange={e => setEvForm({ ...evForm, technician: e.target.value })} /></div>
            </div>
            <div><Label>Notas</Label><Input value={evForm.notes} onChange={e => setEvForm({ ...evForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowEventDlg(false)}>Cancelar</Button><Button onClick={addEvent} data-testid="save-event-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSpareDlg} onOpenChange={setShowSpareDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar sobressalente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={spForm.name} onChange={e => setSpForm({ ...spForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ref.</Label><Input value={spForm.part_number} onChange={e => setSpForm({ ...spForm, part_number: e.target.value })} /></div>
              <div><Label>Fornecedor</Label><Input value={spForm.supplier} onChange={e => setSpForm({ ...spForm, supplier: e.target.value })} /></div>
              <div><Label>Stock</Label><Input type="number" value={spForm.quantity_in_stock} onChange={e => setSpForm({ ...spForm, quantity_in_stock: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Mínimo</Label><Input type="number" value={spForm.min_quantity} onChange={e => setSpForm({ ...spForm, min_quantity: parseInt(e.target.value) || 1 })} /></div>
              <div className="col-span-2"><Label>Custo unitário (€)</Label><Input type="number" step="0.01" value={spForm.unit_cost} onChange={e => setSpForm({ ...spForm, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowSpareDlg(false)}>Cancelar</Button><Button onClick={addSpare} data-testid="save-spare-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showLocDlg} onOpenChange={setShowLocDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Localização do molde</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Localização</Label><Input value={locForm.location} onChange={e => setLocForm({ ...locForm, location: e.target.value })} placeholder="Ex: Estante A3, Máquina 5" /></div>
            <div><Label>Notas</Label><Input value={locForm.notes} onChange={e => setLocForm({ ...locForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={saveLocation} data-testid="save-location-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPlanDlg} onOpenChange={setShowPlanDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Plano de manutenção preventiva</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cada X ciclos</Label><Input type="number" value={planForm.every_cycles} onChange={e => setPlanForm({ ...planForm, every_cycles: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Cada X dias</Label><Input type="number" value={planForm.every_days} onChange={e => setPlanForm({ ...planForm, every_days: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <Label>Tarefas</Label>
              {planForm.tasks.map((t, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <Input value={t} onChange={e => { const nt = [...planForm.tasks]; nt[i] = e.target.value; setPlanForm({ ...planForm, tasks: nt }); }} />
                  <Button size="sm" variant="ghost" onClick={() => setPlanForm({ ...planForm, tasks: planForm.tasks.filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setPlanForm({ ...planForm, tasks: [...planForm.tasks, ""] })} className="mt-2"><Plus className="h-3 w-3 mr-1" /> Tarefa</Button>
            </div>
          </div>
          <DialogFooter><Button onClick={savePlan} data-testid="save-plan-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDocDlg} onOpenChange={setShowDocDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Documento / Foto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo</Label>
              <Select value={docForm.doc_type} onValueChange={v => setDocForm({ ...docForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="photo">Foto</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="drawing">Desenho técnico</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Título</Label><Input value={docForm.title} onChange={e => setDocForm({ ...docForm, title: e.target.value })} /></div>
            <div><Label>URL</Label><Input value={docForm.url} onChange={e => setDocForm({ ...docForm, url: e.target.value })} placeholder="https://..." /></div>
          </div>
          <DialogFooter><Button onClick={addDoc} data-testid="save-doc-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
